/**
 * TASKS-V2 E3 — countdown de prazo de tarefa.
 *
 * Substitui data absoluta ("06 ago.") por texto relativo ("vence em 3h",
 * "atrasada há 2 dias", "em 5 dias", "26 jul").
 *
 * Cores via urgency (consumido pelo TaskCard / HeroFocusCard):
 *   overdue → red-400
 *   today   → amber-400 (vence hoje, ainda não passou)
 *   soon    → cyan-400  (amanhã até 3 dias)
 *   future  → slate-500 (4+ dias)
 *
 * Sem dependência externa de date-fns — Date nativo do JS é suficiente
 * pra fechamentos curtos. Locale pt-BR só pra format de "26 jul".
 *
 * Contract:
 *   formatCountdown(datetime_due: string|null, now?: Date) →
 *     null  se datetime_due ausente
 *     { text: string, urgency: "overdue"|"today"|"soon"|"future" }
 */

const MONTHS_PT = [
  "jan", "fev", "mar", "abr", "mai", "jun",
  "jul", "ago", "set", "out", "nov", "dez",
];

function _isSameDate(a, b) {
  return a.getFullYear() === b.getFullYear() &&
         a.getMonth() === b.getMonth() &&
         a.getDate() === b.getDate();
}

function _addDays(d, days) {
  const r = new Date(d);
  r.setDate(r.getDate() + days);
  return r;
}

function _formatAbsolute(d) {
  return `${d.getDate()} ${MONTHS_PT[d.getMonth()]}`;
}

export function formatCountdown(datetime_due, now = new Date()) {
  if (!datetime_due) return null;
  let due;
  try {
    due = new Date(datetime_due);
    if (Number.isNaN(due.getTime())) return null;
  } catch {
    return null;
  }

  const diffMs = due.getTime() - now.getTime();
  const absMs = Math.abs(diffMs);
  const overdue = diffMs < 0;

  const minutes = Math.floor(absMs / (60 * 1000));
  const hours = Math.floor(absMs / (60 * 60 * 1000));
  const days = Math.floor(absMs / (24 * 60 * 60 * 1000));

  if (overdue) {
    let text;
    if (minutes < 60) {
      // < 1h
      text = `atrasada há ${Math.max(1, minutes)}min`;
    } else if (hours < 24) {
      text = `atrasada há ${hours}h`;
    } else if (days < 7) {
      text = `atrasada há ${days} ${days === 1 ? "dia" : "dias"}`;
    } else {
      text = `atrasada (${_formatAbsolute(due)})`;
    }
    return { text, urgency: "overdue" };
  }

  // Future
  // Distingue "vence hoje" de "amanhã" via comparação de dias do calendário
  // (não só diff_ms < 24h — porque "agora 23h" vs "amanhã 00h30" é < 2h
  // mas conceitualmente "amanhã").
  if (_isSameDate(due, now)) {
    if (minutes < 60) {
      return { text: `vence em ${Math.max(1, minutes)}min`, urgency: "today" };
    }
    return { text: `vence em ${hours}h`, urgency: "today" };
  }

  const tomorrow = _addDays(now, 1);
  if (_isSameDate(due, tomorrow)) {
    // "amanhã 14h"
    const hh = String(due.getHours()).padStart(2, "0");
    return { text: `amanhã ${hh}h`, urgency: "soon" };
  }

  // Dias completos restantes (a partir do início do dia de `now`).
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const dueDayStart = new Date(due);
  dueDayStart.setHours(0, 0, 0, 0);
  const fullDays = Math.round(
    (dueDayStart.getTime() - startOfToday.getTime()) / (24 * 60 * 60 * 1000),
  );

  if (fullDays === 1) {
    // Caso raro: due time já passou hoje cedo mas data calendário é amanhã.
    // Cobre via fallback "em 1 dia".
    return { text: "em 1 dia", urgency: "soon" };
  }
  if (fullDays >= 2 && fullDays <= 6) {
    return { text: `em ${fullDays} dias`, urgency: "soon" };
  }
  return { text: _formatAbsolute(due), urgency: "future" };
}

export const URGENCY_COLOR = {
  overdue: "text-red-400",
  today: "text-amber-400",
  soon: "text-cyan-400",
  future: "text-slate-500",
};

/**
 * formatAbsoluteTooltip — texto pro tooltip do countdown (data completa
 * em pt-BR). Não inclui horário se for 23:59 (artefato comum de
 * "end of day" no backend).
 */
export function formatAbsoluteTooltip(datetime_due) {
  if (!datetime_due) return "";
  let d;
  try {
    d = new Date(datetime_due);
    if (Number.isNaN(d.getTime())) return "";
  } catch {
    return "";
  }
  const weekday = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"][d.getDay()];
  const day = d.getDate();
  const month = MONTHS_PT[d.getMonth()];
  const hour = d.getHours();
  const min = d.getMinutes();
  const isEndOfDay = hour === 23 && min === 59;
  if (isEndOfDay) {
    return `${weekday}, ${day} de ${month}`;
  }
  return `${weekday}, ${day} de ${month}, ${String(hour).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}
